// AssoMembres.jsx — Gestion des membres + invitations (refonte éditoriale)
import { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { api } from '@/lib/api';
import { Plus, Search, Loader2, X, Mail, Send, Copy, Check, Link2, Clock, UserPlus } from 'lucide-react';
import { toast } from 'sonner';

export default function AssoMembres() {
  const { asso } = useOutletContext();
  const [tab, setTab] = useState('membres');
  const [list, setList] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [formules, setFormules] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [m, i, f] = await Promise.all([
        api.get('/membres', { params: { search } }),
        api.get('/invitations/admin').catch(() => ({ data: [] })),
        api.get('/formules').catch(() => ({ data: [] })),
      ]);
      setList(m.data);
      setInvitations(i.data);
      setFormules(f.data);
    } catch { toast.error('Erreur'); }
    finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const publicUrl = asso?.slug ? `${window.location.origin}/asso/${asso.slug}` : '';
  const copyShareLink = () => {
    navigator.clipboard.writeText(publicUrl);
    setShareCopied(true);
    setTimeout(() => setShareCopied(false), 1500);
    toast.success('Lien copié');
  };

  const cancelInvitation = async (id) => {
    if (!window.confirm('Annuler cette invitation ?')) return;
    try {
      await api.delete(`/invitations/admin/${id}`);
      toast.success('Invitation annulée');
      load();
    } catch { toast.error('Erreur'); }
  };
  const resendInvitation = async (id) => {
    try {
      const { data } = await api.post(`/invitations/admin/${id}/resend`);
      toast.success(data.email_mocked ? 'Renvoyé (mode mocké)' : 'Email renvoyé');
    } catch { toast.error('Erreur'); }
  };

  const pendingCount = invitations.filter((i) => !i.used && new Date(i.expires_at) > new Date()).length;

  return (
    <div className="p-4 sm:p-8 lg:p-12 max-w-7xl mx-auto" data-testid="asso-membres">

      {/* En-tête éditorial */}
      <div className="flex items-start justify-between mb-8 slide-up flex-wrap gap-4">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] mb-3" style={{ color: 'var(--theme-primary)' }}>Membres</div>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight text-[#0F172A] leading-[1.02]">
            {list.length} <span className="italic font-medium" style={{ color: 'var(--theme-primary)' }}>adhérents.</span>
          </h1>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => setShowInvite(true)} className="btn-secondary" data-testid="invite-membre-btn">
            <Mail className="w-4 h-4" /> Inviter par email
          </button>
          <button onClick={() => setShowAdd(true)} className="btn-primary" data-testid="add-membre-btn">
            <Plus className="w-4 h-4" /> Ajouter
          </button>
        </div>
      </div>

      {/* Lien public partageable */}
      {publicUrl && (
        <div className="mb-6 p-5 rounded-2xl border border-[#E2E8F0] bg-white flex items-center gap-4 flex-wrap" data-testid="public-share-link">
          <div className="w-10 h-10 rounded-xl grid place-items-center shrink-0" style={{ background: 'var(--theme-primary-soft)', color: 'var(--theme-primary)' }}>
            <Link2 className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#475569] mb-0.5">Lien public d'inscription (à partager)</div>
            <div className="text-sm font-mono text-[#0F172A] truncate">{publicUrl}</div>
          </div>
          <button onClick={copyShareLink} className="btn-secondary !text-xs" data-testid="copy-public-link">
            {shareCopied ? <><Check className="w-3 h-3 text-[#10B981]" /> Copié</> : <><Copy className="w-3 h-3" /> Copier</>}
          </button>
        </div>
      )}

      {/* Tabs */}
      <div className="flex border-b border-[#E2E8F0] mb-6">
        <TabBtn active={tab === 'membres'} onClick={() => setTab('membres')} count={list.length} testid="tab-membres-adherents">Adhérents</TabBtn>
        <TabBtn active={tab === 'invitations'} onClick={() => setTab('invitations')} count={pendingCount} testid="tab-membres-invitations">Invitations</TabBtn>
      </div>

      {tab === 'membres' && (
        <>
          <div className="bg-white border border-[#E2E8F0] rounded-2xl p-4 mb-5 flex items-center gap-3">
            <Search className="w-4 h-4 text-[#94A3B8]" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && load()}
              placeholder="Rechercher un membre…"
              className="flex-1 outline-none text-sm bg-transparent"
              data-testid="membres-search-input"
            />
            {search && <button onClick={() => { setSearch(''); load(); }} className="text-[#94A3B8] hover:text-[#475569]"><X className="w-4 h-4" /></button>}
            <button onClick={load} className="btn-secondary !py-1.5 !px-4 !text-xs">Rechercher</button>
          </div>

          <div className="bg-white border border-[#E2E8F0] rounded-3xl overflow-hidden">
            {loading ? (
              <div className="py-16 grid place-items-center"><Loader2 className="w-5 h-5 animate-spin text-[#94A3B8]" /></div>
            ) : list.length === 0 ? (
              <div className="py-16 text-center">
                <UserPlus className="w-10 h-10 mx-auto mb-3 text-[#CBD5E1]" />
                <p className="text-sm text-[#475569]">Aucun membre. Invitez vos premiers adhérents par email ou via le lien public.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full ap-table" data-testid="membres-table">
                  <thead>
                    <tr>
                      <th>Membre</th><th>Email</th><th>Formule</th><th>Statut</th><th className="text-right">Cotisation</th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.map((m) => (
                      <tr key={m.id} data-testid={`membre-row-${m.id}`}>
                        <td>
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-full grid place-items-center text-xs font-bold" style={{ background: 'var(--theme-primary-soft)', color: 'var(--theme-primary)' }}>
                              {(m.prenom?.[0] || '?') + (m.nom?.[0] || '')}
                            </div>
                            <div>
                              <div className="font-semibold text-[#0F172A]">{m.prenom} {m.nom}</div>
                              <div className="text-xs text-[#94A3B8] capitalize">{m.role}</div>
                            </div>
                          </div>
                        </td>
                        <td className="text-sm text-[#475569]">{m.email}</td>
                        <td><span className="badge badge-neutral capitalize">{m.formule}</span></td>
                        <td><span className={`badge ${m.statut === 'actif' ? 'badge-success' : 'badge-warning'}`}>{m.statut}</span></td>
                        <td className="text-right font-semibold">{Number(m.montant_cotisation || 0).toFixed(0)}€</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {tab === 'invitations' && (
        <div className="bg-white border border-[#E2E8F0] rounded-3xl overflow-hidden" data-testid="invitations-list">
          {invitations.length === 0 ? (
            <div className="py-16 text-center">
              <Mail className="w-10 h-10 mx-auto mb-3 text-[#CBD5E1]" />
              <p className="text-sm text-[#475569] mb-4">Aucune invitation envoyée.</p>
              <button onClick={() => setShowInvite(true)} className="btn-primary">
                <Mail className="w-4 h-4" /> Inviter par email
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full ap-table">
                <thead>
                  <tr>
                    <th>Email invité</th><th>Statut</th><th>Formule</th><th>Envoyé le</th><th></th>
                  </tr>
                </thead>
                <tbody>
                  {invitations.map((inv) => {
                    const expired = !inv.used && new Date(inv.expires_at) < new Date();
                    const formule = formules.find((f) => f.id === inv.formule_id);
                    return (
                      <tr key={inv.id} data-testid={`invitation-row-${inv.id}`}>
                        <td className="font-mono text-sm">{inv.email}</td>
                        <td>
                          {inv.used ? <span className="badge badge-success">Acceptée</span>
                           : expired ? <span className="badge badge-danger">Expirée</span>
                           : <span className="badge badge-info"><Clock className="w-3 h-3" /> En attente</span>}
                        </td>
                        <td>{formule?.nom || <span className="text-[#94A3B8]">—</span>}</td>
                        <td className="text-xs text-[#94A3B8]">{new Date(inv.created_at).toLocaleDateString('fr-FR')}</td>
                        <td className="text-right">
                          {!inv.used && !expired && (
                            <div className="flex justify-end gap-1">
                              <button onClick={() => resendInvitation(inv.id)} className="btn-ghost !text-xs" data-testid={`resend-${inv.id}`}><Send className="w-3 h-3" /> Renvoyer</button>
                              <button onClick={() => cancelInvitation(inv.id)} className="btn-ghost !text-xs text-[#DC2626] hover:bg-[#FEF2F2]"><X className="w-3 h-3" /> Annuler</button>
                            </div>
                          )}
                          {(inv.used || expired) && (
                            <button onClick={() => cancelInvitation(inv.id)} className="btn-ghost !text-xs text-[#94A3B8]"><X className="w-3 h-3" /></button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {showAdd && <AddMembre onClose={() => { setShowAdd(false); load(); }} />}
      {showInvite && <InviteModal formules={formules} onClose={() => { setShowInvite(false); load(); }} />}
    </div>
  );
}

function TabBtn({ active, onClick, count, children, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`relative px-5 py-3 text-sm font-semibold transition-colors ${active ? 'text-[#0F172A]' : 'text-[#94A3B8] hover:text-[#475569]'}`}>
      {children}
      <span className={`ml-2 text-[10px] font-black px-2 py-0.5 rounded-full ${active ? 'bg-[var(--theme-primary)] text-white' : 'bg-[#F1F5F9] text-[#94A3B8]'}`}>{count}</span>
      {active && <span className="absolute -bottom-px inset-x-0 h-0.5 bg-[var(--theme-primary)]" />}
    </button>
  );
}

function AddMembre({ onClose }) {
  const [form, setForm] = useState({ email: '', prenom: '', nom: '', telephone: '', formule: 'participation', montant_cotisation: 0 });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try { await api.post('/membres', form); toast.success('Membre ajouté'); onClose(); }
    catch (err) { toast.error(err?.response?.data?.error || 'Erreur'); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-[#0F172A]/60 backdrop-blur-md grid place-items-center p-4 fade-in" onClick={onClose}>
      <form onSubmit={submit} className="bg-white rounded-3xl shadow-2xl p-7 max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="add-membre-modal">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-display text-2xl font-bold text-[#0F172A]">Ajouter un membre</h3>
          <button type="button" onClick={onClose} className="text-[#94A3B8]"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <input required type="text" placeholder="Prénom" className="ap-input" value={form.prenom} onChange={(e) => setForm({ ...form, prenom: e.target.value })} data-testid="add-membre-prenom" />
            <input required type="text" placeholder="Nom" className="ap-input" value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} data-testid="add-membre-nom" />
          </div>
          <input required type="email" placeholder="Email" className="ap-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="add-membre-email" />
          <input type="tel" placeholder="Téléphone (optionnel)" className="ap-input" value={form.telephone} onChange={(e) => setForm({ ...form, telephone: e.target.value })} />
          <div className="grid grid-cols-2 gap-3">
            <select className="ap-input" value={form.formule} onChange={(e) => setForm({ ...form, formule: e.target.value })} data-testid="add-membre-formule">
              <option value="gratuit">Gratuit</option><option value="participation">Participation</option><option value="mensuel">Mensuel</option><option value="trimestriel">Trimestriel</option><option value="annuel">Annuel</option>
            </select>
            <input type="number" placeholder="Cotisation €" className="ap-input" value={form.montant_cotisation} onChange={(e) => setForm({ ...form, montant_cotisation: e.target.value })} />
          </div>
        </div>
        <div className="flex gap-2 justify-end mt-6 pt-5 border-t border-[#F1F5F9]">
          <button type="button" onClick={onClose} className="btn-secondary">Annuler</button>
          <button type="submit" disabled={saving} className="btn-primary" data-testid="add-membre-submit">{saving ? 'Ajout…' : 'Ajouter'}</button>
        </div>
      </form>
    </div>
  );
}

function InviteModal({ formules, onClose }) {
  const [form, setForm] = useState({ email: '', formule_id: '', message_perso: '' });
  const [saving, setSaving] = useState(false);
  const [sent, setSent] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post('/invitations/admin', form);
      setSent(data);
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Erreur');
    } finally { setSaving(false); }
  };

  const copyUrl = () => { navigator.clipboard.writeText(sent.invite_url); toast.success('Lien copié'); };

  return (
    <div className="fixed inset-0 z-50 bg-[#0F172A]/60 backdrop-blur-md grid place-items-center p-4 fade-in" onClick={onClose}>
      <div className="bg-white rounded-3xl shadow-2xl p-7 max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="invite-modal">
        {sent ? (
          <>
            <div className="w-14 h-14 rounded-2xl bg-[#10B981]/10 grid place-items-center mx-auto mb-5">
              <Check className="w-7 h-7 text-[#10B981]" />
            </div>
            <h3 className="font-display text-2xl font-bold text-center mb-3 text-[#0F172A]">
              Invitation <span className="italic font-medium" style={{ color: 'var(--theme-primary)' }}>envoyée.</span>
            </h3>
            {sent.email_mocked ? (
              <p className="text-sm text-[#475569] text-center mb-5">
                <strong className="text-[#B45309]">Mode mocké</strong> — Brevo n'est pas configuré pour votre asso. Voici le lien que vous pouvez transmettre manuellement à l'invité :
              </p>
            ) : (
              <p className="text-sm text-[#475569] text-center mb-5">Email envoyé à {sent.invitation.email}. Vous pouvez aussi partager le lien direct :</p>
            )}
            <div className="bg-[#F9F9F8] border border-[#E2E8F0] rounded-xl p-3 mb-5 font-mono text-xs break-all" data-testid="invite-result-url">{sent.invite_url}</div>
            <div className="flex gap-2">
              <button onClick={copyUrl} className="btn-secondary flex-1"><Copy className="w-4 h-4" /> Copier le lien</button>
              <button onClick={onClose} className="btn-primary" data-testid="invite-done">Fermer</button>
            </div>
          </>
        ) : (
          <form onSubmit={submit}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-display text-2xl font-bold text-[#0F172A]">Inviter par email</h3>
              <button type="button" onClick={onClose} className="text-[#94A3B8]"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-[0.14em] text-[#475569] mb-2">Email du futur adhérent *</label>
                <input required type="email" className="ap-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="nouveau@exemple.fr" data-testid="invite-email" />
              </div>
              {formules.length > 0 && (
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-[0.14em] text-[#475569] mb-2">Formule proposée (optionnel)</label>
                  <select className="ap-input" value={form.formule_id} onChange={(e) => setForm({ ...form, formule_id: e.target.value })} data-testid="invite-formule">
                    <option value="">— Aucune formule pré-sélectionnée —</option>
                    {formules.filter((f) => f.actif).map((f) => (
                      <option key={f.id} value={f.id}>{f.nom} · {f.prix_cts === 0 ? 'Gratuit' : `${(f.prix_cts / 100).toFixed(0)}€${f.periode_label || ''}`}</option>
                    ))}
                  </select>
                </div>
              )}
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-[0.14em] text-[#475569] mb-2">Message personnel (optionnel)</label>
                <textarea rows={3} className="ap-input" value={form.message_perso} onChange={(e) => setForm({ ...form, message_perso: e.target.value })} placeholder="Quelques mots de bienvenue…" maxLength={500} data-testid="invite-message" />
              </div>
            </div>
            <div className="flex gap-2 justify-end mt-6 pt-5 border-t border-[#F1F5F9]">
              <button type="button" onClick={onClose} className="btn-secondary">Annuler</button>
              <button type="submit" disabled={saving} className="btn-primary" data-testid="invite-submit">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4" /> Envoyer</>}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
